import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import axiosInstance from '../../utils/axios';

// Cookie configuration
// sameSite: 'lax' (not 'strict') is required for mobile browsers.
// 'strict' cookies are dropped on cross-origin navigations (e.g., payment redirects,
// Google OAuth), causing users to appear logged out on mobile.
const COOKIE_OPTIONS = {
  expires: 7,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax'
};

// Helper function to save user data to individual cookies
const saveUserToCookies = (userData) => {
  Cookies.set('artote_user_id', userData.id, COOKIE_OPTIONS);
  Cookies.set('artote_user_name', userData.name, COOKIE_OPTIONS);
  Cookies.set('artote_user_email', userData.email, COOKIE_OPTIONS);
  Cookies.set('artote_user_mobile', userData.mobile, COOKIE_OPTIONS);
  Cookies.set('artote_user_avatar', userData.avatar || '', COOKIE_OPTIONS);
  Cookies.set('artote_user_isLoggedIn', userData.isLoggedIn.toString(), COOKIE_OPTIONS);
};

// Helper function to load user data from individual cookies
const loadUserFromCookies = () => {
  const id = Cookies.get('artote_user_id');
  const name = Cookies.get('artote_user_name');
  const email = Cookies.get('artote_user_email');
  const mobile = Cookies.get('artote_user_mobile');
  const avatar = Cookies.get('artote_user_avatar');
  const isLoggedIn = Cookies.get('artote_user_isLoggedIn');

  if (id && name && email) {
    return {
      id,
      name,
      email,
      mobile,
      avatar: avatar || '',
      isLoggedIn: isLoggedIn === 'true'
    };
  }
  return null;
};

// Helper function to clear user cookies
const clearUserCookies = () => {
  Cookies.remove('artote_user_id');
  Cookies.remove('artote_user_name');
  Cookies.remove('artote_user_email');
  Cookies.remove('artote_user_mobile');
  Cookies.remove('artote_user_avatar');
  Cookies.remove('artote_user_isLoggedIn');
  Cookies.remove('artote_token');
};

// Async thunk to fetch user profile
export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (token, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/user/profile', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const userData = response.data.data;

      return {
        id: userData._id,
        name: userData.name,
        email: userData.email,
        mobile: userData.mobile,
        googleId: userData.googleId,
        authProvider: userData.authProvider,
        avatar: userData.avatar,
        userType: userData.userType,
        isActive: userData.isActive,
        isLoggedIn: userData.isLoggedIn,
        addresses: userData.addresses || [],
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
        lastLogin: userData.lastLogin
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;

      // Save token to cookie
      Cookies.set('artote_token', action.payload.token, COOKIE_OPTIONS);

      // Save user data to individual cookies
      saveUserToCookies(action.payload.user);
    },
    setToken: (state, action) => {
      state.token = action.payload;
      Cookies.set('artote_token', action.payload, COOKIE_OPTIONS);
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };

      // Update individual cookies
      saveUserToCookies(state.user);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      clearUserCookies();
    },
    loadUserFromStorage: (state) => {
      const token = Cookies.get('artote_token');
      const user = loadUserFromCookies();

      if (token && user) {
        state.token = token;
        state.user = user;
        state.isAuthenticated = true;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        const cookieData = {
          id: action.payload.id,
          name: action.payload.name,
          email: action.payload.email,
          mobile: action.payload.mobile,
          avatar: action.payload.avatar,
          isLoggedIn: action.payload.isLoggedIn
        };
        saveUserToCookies(cookieData);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // If profile fetch fails, clear auth
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        clearUserCookies();
      });
  }
});

export const { setUser, setToken, updateUser, logout, loadUserFromStorage } = userSlice.actions;
export default userSlice.reducer;
